import { PageParameterId } from '@pendo/middleware';
import { MetadataTypeId } from '@/types/analytics/analytics.types';

// Descriptor for the event property promoted to "object property".
// `kind` routes resolution to the right agg field path.
export interface ObjectAnalyticsObjectProperty {
    field: string;
    /** Defaults to `MetadataTypeId.Event` (i.e. `properties.${field}`). */
    kind?: MetadataTypeId;
    group?: string;
    label?: string;
    // Source metadata kind (`visitor` / `account` / `parentAccount`) for
    // `MetadataTypeId.Historical`. Selects the `__sg__.<kind>metadata`
    // segment of the row path. Ignored for other kinds.
    metadataKind?: string;
}

// Cardinality ceiling for an event property the user hasn't explicitly set
// as an object property. Above this, the per-object breakdown is too sparse
// to be useful, so it's kept out of the selector.
export const OBJECT_PROPERTY_CARDINALITY_LIMIT = 100000;

// Whether an event property may appear in the object-property selector. A
// field the user has set as an object property always qualifies (cardinality
// is irrelevant). Otherwise it's gated on cardinality: a known estimate above
// the limit is excluded; a missing estimate is allowed (we only reject when
// we have proof it's too high). Historical metadata carries neither field and
// is handled separately.
export function isEventPropertyAllowedAsObjectProperty ({
    objectProperty,
    cardinalityEstimate,
}: {
    objectProperty?: number | null;
    cardinalityEstimate?: number | null;
}): boolean {
    if (objectProperty) return true;
    if (typeof cardinalityEstimate !== 'number') return true;

    return cardinalityEstimate <= OBJECT_PROPERTY_CARDINALITY_LIMIT;
}

// Whether an event property has logged data for at least one of the apps in
// scope, per its `lastEventTimes` map (app id -> last-seen timestamp). With no
// apps in scope this is always true (nothing to filter on). A missing or
// non-overlapping usage map means the selected apps never populated it.
// Callers use this two ways: undesignated event properties are dropped when it
// returns false, while designated object properties stay in the selector but
// are shown disabled (so an intentional pick is never silently hidden).
export function eventPropertyHasDataForApps (
    lastEventTimes: Record<string, number> | null | undefined,
    selectedAppIds: Set<string>,
): boolean {
    if (!selectedAppIds || selectedAppIds.size === 0) return true;
    if (!lastEventTimes || typeof lastEventTimes !== 'object') return false;

    return Object.keys(lastEventTimes).some((appId) => selectedAppIds.has(String(appId)));
}

// Best-effort client-side proof that an event property rolls up to app-level `events`.
export function eventPropertyRollsUpToEventsSource ({
    objectProperty,
    eventUrlRules,
}: {
    objectProperty?: number | null;
    eventUrlRules?: ReadonlyArray<string> | null;
}): boolean {
    if (objectProperty) return true;

    return Array.isArray(eventUrlRules) && eventUrlRules.length > 0;
}

export function resolveObjectPropertyField ({ field, kind, group, metadataKind }: ObjectAnalyticsObjectProperty): string {
    switch (kind) {
        case MetadataTypeId.EventTopLevel:
            return field === 'region' ? 'country_region' : field;
        case MetadataTypeId.Automatic:
            return `parsedUserAgent.${field}`;
        case MetadataTypeId.PageParameter:
            return PageParameterId;
        case MetadataTypeId.Historical:
            // Promoted (historical) metadata is baked onto event rows by
            // the agg backend under `__sg__` — no bulk-expand needed,
            // unlike live `visitor.*` / `account.*` reads. Mirrors the
            // `historicalMetadata` group-by path in middleware's
            // `group-by-dimension.ts`. `group` and `metadataKind` are both
            // required to address the field; bail loudly rather than emit a
            // silently wrong `...metadata.undefined__<field>` path.
            if (!group || !metadataKind) {
                throw new Error(
                    `resolveObjectPropertyField: historical metadata requires group and metadataKind (got group=${group}, metadataKind=${metadataKind})`,
                );
            }

            return `properties.__sg__.${metadataKind.toLowerCase()}metadata.${group}__${field}`;
        default:
            return `properties.${field}`;
    }
}
